/* eslint-disable import/no-internal-modules */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable @typescript-eslint/no-shadow */
import { Styles } from '#~/agent/components/index.js';
import { Lang } from '#~/agent/index.js';
import { Meter } from '#~/audio/components/meter/index.js';
import { Settings } from '#~/common/card/index.js';
import { ConnectionStatus } from '#~/common/connection-status/index.js';
import { codecDisplayName } from '#~/room/components/production/components/codec-warning.js';
import { useRecording } from '#~/room/components/production/recording/use-recording.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import { Icon } from '@studio/design-system/icon';
import * as Tabs from '@studio/design-system/tabs';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import { isTransient } from '@studio/models/device';
import {
  StatusEgressState,
  StatusEncoderOperationMode
} from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isNotNil } from '@tryiris-ai/js-types';
import prettyBytes from 'pretty-bytes';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { twMerge } from 'cn';
import {
  useAddAudio,
  useSettings as useAudioSettings
} from '../../../../room/components/audio/hooks.js';
import {
  useAddVideo,
  useSettings as useVideoSettings
} from '../../../../room/components/video/hooks.js';
import { locateDevice } from '../../../../room/utils.js';
import { Cell, Table } from '../layout/index.js';
import { Recording } from '../recording/recording.js';
import type { Wiring$Device } from '../types.js';
import { Tab } from '../types.js';
import { resolutionLookup } from '../wiring.js';
import { NA } from './constants.js';
import type { Props } from './types.js';

import { useMultiProxyStateQuery } from '#~/statequery/hook.js';
import { mapResolution } from './resolution-mapping.js';
// Manual low-bandwidth toggle — lets users override auto-detection (W2)
import { BandwidthToggle } from './bandwidth-toggle.js';
import { useAudioDeviceStats, useVideoDeviceStats } from './device-stats-hook.js';

/** @internal Device connection status component */
const DeviceConnectionStatus = ({
  cells,
  device,
  onClickSettings,
  agents
}: {
  cells: Record<string, { containerClassName?: string; element: string | ReactElement }>;
  device: Wiring$Device;
  onClickSettings: (deviceId: string) => void;
  agents: Record<Agent['id'], Agent>;
}): ReactElement => {
  const agent = agents[device.agentId];

  return (
    <tr>
      <th colSpan={Object.entries(cells).length}>
        {/* Bridge name instead of protocol/source */}
        <div className='flex items-center justify-between'>
          <div className='mb-2 flex flex-col items-baseline'>
            <div className='mb-4'>
              <ConnectionStatus connected={device.connected} className='mr-4 text-[10px]' />
              <span
                className={twMerge(
                  'text-md font-bold text-light',
                  device.embedded && !device.connected && 'text-secondary'
                )}
              >
                {device.name}
              </span>
            </div>
            <div className='flex items-center'>
              <Icon icon='bridge' className='mr-2 text-md text-muted' />
              <span className='text-sm text-muted'>{agent?.name ?? NA}</span>
            </div>
          </div>
          <div className='flex items-center'>
            <span className='mr-4 font-medium text-error'>
              {!device.connected && device.embedded ? 'Offline' : ''}
            </span>

            <Settings
              className='w-18 opacity-100'
              icon='settings'
              onClick={(): void => {
                onClickSettings(device.id);
              }}
            />
          </div>
        </div>
      </th>
    </tr>
  );
};

/** @internal Device upstream summary component */
const DeviceUpstreamSummary = ({
  cells,
  device,
  warning
}: {
  cells: Record<string, { containerClassName?: string; element: ReactElement | string }>;
  device: Wiring$Device;
  warning?: T_Maybe<ReactElement>;
}): ReactElement => {
  const cellsCount = Object.entries(cells).length;

  const message = `
    Video/Audio quality uploaded from the Bridge to Iris
    Cloud, influenced by the Bridge's upload bandwidth.
    (This represents the quality once it reaches our Cloud.)
  `;

  return (
    <>
      <tr>
        {Object.entries(cells).map(([label, { containerClassName, element }], index) => (
          <Cell
            containerClassName={containerClassName}
            key={`device_status_${device.id}_${label}`}
            label={label}
            message={message}
            value={element}
            index={index}
            width={`${(100 / cellsCount).toFixed(1)}%`}
          />
        ))}
      </tr>
      {warning && (
        <tr className='border-t border-base-50'>
          <td colSpan={cellsCount} width={`${(100 / cellsCount).toFixed(1)}%`}>
            {warning}
          </td>
        </tr>
      )}
    </>
  );
};

/** @internal Device summary component */
const DeviceDownstreamSummary = ({
  cells,
  device,
  warning
}: {
  cells: Record<string, { containerClassName?: string; element: ReactElement | string | null }>;
  device: Wiring$Device;
  warning?: T_Maybe<ReactElement>;
}): ReactElement => {
  const cellsCount = Object.entries(cells).length;

  const message = `
    Video/Audio quality downloaded from Iris Cloud to your
    Studio, influenced by your current download speed.
    (This reflects what you currently see in your Studio.)
  `;

  return (
    <>
      <tr className='border-b border-t border-base-50'>
        {Object.entries(cells).map(([label, { containerClassName, element }], index) => (
          <Cell
            containerClassName={containerClassName}
            key={`device_status_${device.id}_${label}`}
            label={label}
            index={index}
            message={message}
            value={element}
            width={`${(100 / cellsCount).toFixed(1)}%`}
          />
        ))}
      </tr>
      {warning && (
        <tr>
          <td
            colSpan={cellsCount}
            width={`${(100 / cellsCount).toFixed(1)}%`}
            className='w-full border-t border-base-50'
          >
            {warning}
          </td>
        </tr>
      )}
    </>
  );
};

type DeviceSummaryProps = {
  cells: Record<string, { containerClassName?: string; element: ReactElement | string }>;
  downStreamCells: Record<string, { containerClassName?: string; element: ReactElement | string }>;
  device: Wiring$Device;
  onClickSettings: (deviceId: string) => void;
  warning?: Record<string, T_Maybe<ReactElement>>;
  agents: Record<Agent['id'], Agent>;
  advancedStatsCells?: Record<
    string,
    { containerClassName?: string; element: ReactElement | string | null }
  >;
};
/** @internal Device summary component */
const DeviceSummary = ({
  cells,
  downStreamCells,
  device,
  onClickSettings,
  warning,
  advancedStatsCells,
  agents
}: DeviceSummaryProps): ReactElement => {
  return (
    <div className='flex grow flex-col'>
      <Table className='grow'>
        <thead>
          <DeviceConnectionStatus
            cells={cells}
            device={device}
            onClickSettings={onClickSettings}
            agents={agents}
          />
        </thead>
        <tbody>
          <DeviceUpstreamSummary cells={cells} device={device} warning={warning?.upstream} />
          <DeviceDownstreamSummary
            cells={downStreamCells}
            device={device}
            warning={warning?.downstream}
          />
          {advancedStatsCells && (
            <DeviceDownstreamSummary cells={advancedStatsCells!} device={device} />
          )}
        </tbody>
      </Table>
      {warning?.encoder && <div className='mt-8'>{warning.encoder}</div>}
    </div>
  );
};

type DeviceSummaryVideoProps = {
  agents: Props['wiring']['agents'];
  device: Wiring$Device;
  roomId: Props['roomId'];
  onClickSettings: (deviceId: string) => void;
  onClickApplySuggestedSettings: (deviceId: string) => void;
  onClickEnableVideo: (deviceId: string) => void;
  summary: Props['wiring']['summary'];
};

const DeviceSummaryVideoContent = ({
  agents,
  device,
  roomId,
  onClickSettings,
  onClickApplySuggestedSettings,
  onClickEnableVideo,
  summary
}: DeviceSummaryVideoProps): ReactElement => {
  const agent = agents[device.agentId];
  const agentSummary = summary.agentStats?.[device.agentId];

  const additionalCells =
    agentSummary && agent?.embedded
      ? {
          CPU: {
            element: isNotNil(agentSummary.cpuUsage) ? (
              <span className={Number(agentSummary.cpuUsage) > 80 ? 'font-bold text-error' : ''}>
                {agentSummary.cpuUsage}%
              </span>
            ) : (
              NA
            )
          },
          RAM: {
            element: isNotNil(agentSummary.ramUsage) ? (
              <span className={Number(agentSummary.ramUsage) > 90 ? 'font-bold text-error' : ''}>
                {agentSummary.ramUsage}%
              </span>
            ) : (
              NA
            )
          },
          Disk: {
            element: isNotNil(agentSummary.diskSpace)
              ? prettyBytes(agentSummary.diskSpace, { binary: false })
              : NA
          }
        }
      : {};

  const deviceStatusMap = useMultiProxyStateQuery<{
    found: boolean;
    resolution: string;
    bps: number | undefined;
    framerate: number | undefined;
    network: number | undefined;
    codec: string | undefined;
  }>((state, _proxyID) => {
    const { video } = state?.status?.device?.[device.id] ?? {};

    return {
      found: !!video,
      resolution:
        video?.status?.height && video.status.width
          ? mapResolution(video.status.width, video.status.height)
          : NA,
      bps: video?.status?.bps,
      framerate: video?.status?.framerate,
      network: video?.status?.network ?? video?.localStatus?.network,
      codec: video?.status?.codec
    };
  });

  const upstreamStats = Object.values(deviceStatusMap).find((status) => status.found);
  const downstreamStats = useVideoDeviceStats(roomId, device.id as Device['id']);

  const metricsWarning: Record<string, T_Maybe<ReactElement>> = {
    upstream:
      device.connected && device.video?.network && device.video.network < 2.5 ? (
        <div className='flex items-center space-x-2 rounded border-1 border-error p-4 py-8 text-[12px]'>
          <Icon icon='alert-triangle text-error mr-4 text-[16px]' />
          <span>
            Your upstream source quality is poor. This could be due to internet stability.
          </span>
        </div>
      ) : undefined,
    downstream:
      device.connected &&
      downstreamStats &&
      downstreamStats.quality &&
      downstreamStats.quality < 0.5 ? (
        <div className='flex w-full items-center space-x-2 rounded border-1 border-error p-4 py-8 text-[12px]'>
          <Icon icon='alert-triangle text-error mr-4 text-[16px]' />
          <span>
            Your downstream source quality is poor. This could be due to internet stability.
          </span>
        </div>
      ) : undefined,
    encoder:
      agentSummary?.operationMode == StatusEncoderOperationMode.OPERATION_MODE_DEGRADED ? (
        <div className='flex items-center space-x-2 rounded border-1 border-warning p-4 py-8 text-[12px]'>
          <Icon icon='alert-triangle text-warning mr-4 text-[16px]' />
          <span>
            System resources are high.
            {device.hasSettingsToApply ? ' Apply a stable profile to improve performance.' : ''}
          </span>
          <span>
            {device.hasSettingsToApply && (
              <button
                className='hover:text-primary/80 ml-4 text-primary'
                onClick={() => onClickApplySuggestedSettings(device.id)}
                type='button'
              >
                Apply stable profile.
              </button>
            )}
          </span>
        </div>
      ) : agentSummary?.operationMode == StatusEncoderOperationMode.OPERATION_MODE_MINIMAL_SAFE ||
        agentSummary?.operationMode == StatusEncoderOperationMode.OPERATION_MODE_RECOVERY ? (
        <div className='flex items-center space-x-2 rounded border-1 border-error p-4 py-8 text-[12px]'>
          <Icon icon='alert-triangle text-error mr-4 text-[16px]' />
          <span>
            Critical resource usage detected.{' '}
            {device.videoDisabled === true && (
              <>
                Video is paused.{' '}
                <button
                  className='mx-1 hover:text-primary/80 text-primary underline'
                  onClick={() => onClickEnableVideo(device.id)}
                  type='button'
                >
                  Re-enable video
                </button>
                {device.hasSettingsToApply ? <>{' or '}</> : <>.</>}
              </>
            )}
            {device.hasSettingsToApply && (
              <button
                className='mx-1 hover:text-primary/80 text-primary underline'
                onClick={() => {
                  onClickApplySuggestedSettings(device.id);
                  onClickEnableVideo(device.id);
                }}
                type='button'
              >
                Apply a stable profile.
              </button>
            )}
          </span>
        </div>
      ) : undefined
  };

  // Upstream metric cells
  const cells = {
    'Sending': {
      element: 'Sending'
    },
    'Quality Score': {
      element:
        device.connected && isNotNil(upstreamStats?.network)
          ? `${((upstreamStats?.network ?? 0) * 5).toFixed(1)} / 5.0`
          : NA
    },
    'Resolution': { element: upstreamStats?.resolution ?? NA },
    'Bitrate': {
      element:
        device.connected && isNotNil(upstreamStats?.bps)
          ? `${Math.round(upstreamStats?.bps / 1000)} kbps`
          : NA
    },
    'FPS': {
      element:
        device.connected && isNotNil(upstreamStats?.framerate)
          ? `${upstreamStats?.framerate?.toFixed(1)}`
          : NA
    },
    // Match the per-camera monitor's device-keyed readout so HIL reads the
    // upstream codec from either panel (playwright-stag-selectors-only, ai.knowledge/platform).
    'Codec': {
      element: (
        <span
          className='block text-center'
          {...DataSTag(Sel.Monitoring, 'upstream_codec', device.id)}
        >
          {codecDisplayName(upstreamStats?.codec) ?? NA}
        </span>
      )
    },
    ...additionalCells
  };

  // Downstream metric cells
  const downStreamCells = {
    'Receiving': {
      element: 'Receiving'
    },
    'Quality Score': {
      // NOTE: this score is being calculated by the live-downstream-metrics-monitor class
      // Can change this if need be.
      element:
        device.connected && isNotNil(downstreamStats?.quality)
          ? `${(downstreamStats?.quality * 5).toFixed(1)} / 5.0`
          : NA
    },
    'Resolution': {
      element:
        device.connected && isNotNil(downstreamStats?.width) && isNotNil(downstreamStats?.height)
          ? resolutionLookup(downstreamStats.width, downstreamStats.height)
          : NA
    },
    'Bitrate': {
      // TODO: TAM important note here, notice how we are dividing by the correct value here.
      // This is because the `bitrate` value is in bits, not bytes and this is handled in the
      // `livekit-downstream-metrics-monitor.ts` file.
      element:
        device.connected && isNotNil(downstreamStats?.bitrate)
          ? `${Math.round(downstreamStats.bitrate / 1000)} kbps`
          : NA
    },
    'FPS': {
      element:
        device.connected && isNotNil(downstreamStats?.framesPerSecond)
          ? `${downstreamStats.framesPerSecond.toFixed(1)}`
          : NA
    }
  };

  // These statistics are not currently functional. Uncomment when ready to re-enable.
  // const advancedStatsCells = {
  //   "Advanced Stats": { element: null },
  //   "Dropped Frames": {
  //     element:
  //       device.connected && isNotNil(downstreamStats?.framesDropped)
  //         ? `${downstreamStats.framesDropped.toFixed(0)} / min`
  //         : NA
  //   },
  //   "Packet Loss %": {
  //     element:
  //       device.connected && isNotNil(downstreamStats?.packetLoss)
  //         ? `${downstreamStats.packetLoss.toFixed(2)}%`
  //         : NA
  //   },
  //   "Filler 1": { element: null },
  //   "Filler 2": { element: null }
  // };

  return (
    <DeviceSummary
      cells={cells}
      downStreamCells={downStreamCells}
      // advancedStatsCells={advancedStatsCells}
      device={device}
      onClickSettings={onClickSettings}
      warning={metricsWarning}
      agents={agents}
    />
  );
};

/** @internal Device summary video component */
const DeviceSummaryVideo = (props: DeviceSummaryVideoProps): ReactElement => {
  if (!props.device.video) {
    return <></>;
  }

  return <DeviceSummaryVideoContent {...props} />;
};

const DeviceSummaryAudio = ({
  agents,
  device,
  roomId,
  onClickSettings,
  summary
}: {
  agents: Props['wiring']['agents'];
  device: Wiring$Device;
  roomId: Props['roomId'];
  onClickSettings: (deviceId: string) => void;
  summary: Props['wiring']['summary'];
}): ReactElement => {
  const agent = agents[device.agentId];

  const agentSummary = summary.agentStats?.[device.agentId];

  const additionalCells =
    agentSummary && agent?.embedded
      ? {
          CPU: {
            element: isNotNil(agentSummary.cpuUsage) ? (
              <span className={Number(agentSummary.cpuUsage) > 80 ? 'font-bold text-error' : ''}>
                {agentSummary.cpuUsage}%
              </span>
            ) : (
              NA
            )
          },
          RAM: {
            element: isNotNil(agentSummary.ramUsage) ? (
              <span className={Number(agentSummary.ramUsage) > 80 ? 'font-bold text-error' : ''}>
                {agentSummary.ramUsage}%
              </span>
            ) : (
              NA
            )
          },
          Disk: {
            element: isNotNil(agentSummary.diskSpace)
              ? prettyBytes(agentSummary.diskSpace, { binary: false })
              : NA
          }
        }
      : {};

  const deviceStatusMap = useMultiProxyStateQuery<{
    found: boolean;
    bps: number | undefined;
    codec: string | undefined;
  }>((state, _proxyID) => {
    const { audio } = state?.status?.device?.[device.id] ?? {};
    return {
      found: !!audio,
      bps: audio?.status?.bps,
      codec: audio?.status?.codec
    };
  });

  const deviceStatus = Object.values(deviceStatusMap).find((status) => status.found);

  const cells = {
    'Sending': {
      element: 'Sending'
    },
    'Audio Level': {
      element: <Meter deviceId={device.id as Device['id']} />,
      containerClassName: 'w-[100%]'
    },
    'Bitrate': {
      element:
        device.connected && isNotNil(deviceStatus?.bps)
          ? `${Math.round(deviceStatus.bps / 1000)} kbps`
          : NA
    },
    'Codec': { element: deviceStatus?.codec ?? NA },
    ...additionalCells
  };

  const downstreamStats = useAudioDeviceStats(roomId, device.id as Device['id']);

  const downStreamCells = {
    'Receiving': {
      element: 'Receiving'
    },
    'Audio Level': {
      element: <Meter deviceId={device.id as Device['id']} />,
      containerClassName: 'w-[100%]'
    },
    'Bitrate': {
      element:
        device.connected && isNotNil(downstreamStats?.bitrate)
          ? `${Math.round(downstreamStats.bitrate / 1000)} kbps`
          : NA
    },
    'Codec': { element: downstreamStats?.codec ?? NA },
    ...additionalCells
  };

  return (
    <DeviceSummary
      cells={cells}
      downStreamCells={downStreamCells}
      device={device}
      onClickSettings={onClickSettings}
      agents={agents}
    />
  );
};

/**
 * `<AgentSummary>` summary component
 *
 * Not being used/referenced, as of now.
 */
export const AgentSummary = ({
  agent: { id, name, status },
  summary: roomSummary
}: {
  agent: Agent;
  summary: Props['wiring']['summary'];
}): ReactElement => {
  const summary = roomSummary.agentStats?.[id];

  if (!summary) {
    return <></>;
  }

  return (
    <Table className='grow'>
      <thead>
        <tr>
          <th colSpan={6}>
            <div className='flex items-center justify-between'>
              <div>{name}</div>
              <div className={twMerge(Styles.status[status].text, 'font-medium')}>
                {Lang.status[status]}
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <Cell
            label='CPU usage'
            value={isNotNil(summary.cpuUsage) ? `${summary.cpuUsage}%` : NA}
          />

          <Cell
            label='RAM usage'
            value={isNotNil(summary.ramUsage) ? `${summary.ramUsage}%` : NA}
          />

          <Cell
            label='Disk space'
            value={
              isNotNil(summary.diskSpace) ? prettyBytes(summary.diskSpace, { binary: false }) : NA
            }
          />
        </tr>
      </tbody>
    </Table>
  );
};

/** `<Summary>` component. */
export const Summary = ({ roomId, wiring }: Props): ReactElement => {
  const { agents, room, summary, handleApplySettings, handleEnableVideo } = wiring;
  const { sources } = useRecording();

  const recordingDevices = sources.filter(
    (v) =>
      v.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_ACTIVE ||
      v.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_FAILED ||
      v.egress?.recordingStatus?.state === StatusEgressState.EGRESS_STATE_FAILED_TO_START
  );

  const videoCount = room?.tally.video ?? 0;
  const audioCount = room?.tally.audio ?? 0;
  const recordingCount = recordingDevices.length;

  const showOverview = videoCount === 0 && audioCount === 0;

  const [tab, setTab] = useState<Tab>(showOverview ? Tab.Overview : Tab.Video);
  const addVideo = useAddVideo();
  const addAudio = useAddAudio();

  const videoDeviceIds = summary.videoDevices?.map((device) => device.id);

  const videoSettings = useVideoSettings(room?.id);
  const audioSettings = useAudioSettings();

  const handleOpenSettings = (deviceId: string): void => {
    const device = locateDevice(deviceId as Device['id']) ?? {};

    const safeDevice = device as Device;

    const settings = videoDeviceIds?.includes(safeDevice.id) ? videoSettings : audioSettings;

    if (safeDevice) {
      settings(safeDevice);
    }
  };

  const handleApplySuggestedSettings = (deviceId: string): void => {
    const device = locateDevice(deviceId as Device['id']);
    if (device) {
      handleApplySettings(deviceId as Device['id']);
    }
  };

  const handleEnableVideoForDevice = (deviceId: string): void => {
    const device = locateDevice(deviceId as Device['id']);
    if (device) {
      handleEnableVideo(deviceId as Device['id']);
    }
  };

  return (
    <>
      <Tabs.Root
        className='grow overflow-visible'
        onChange={(tab): void => {
          setTab(tab as Tab);
        }}
        value={tab as string}
      >
        {/* List of Tab types: Video, Audio, Recording */}
        <Tabs.List className='mb-16 border-base-50 px-0'>
          {showOverview && <Tabs.Trigger value={Tab.Overview}>Overview</Tabs.Trigger>}
          <Tabs.Trigger disabled={videoCount === 0} value={Tab.Video}>
            Video Sources {videoCount > 0 && `(${videoCount})`}
          </Tabs.Trigger>
          <Tabs.Trigger disabled={audioCount === 0} value={Tab.Audio}>
            Audio Sources {audioCount > 0 && `(${audioCount})`}
          </Tabs.Trigger>
          <Tabs.Trigger disabled={recordingCount === 0} value={Tab.Recording}>
            Recording {recordingCount > 0 && `(${recordingDevices.length})`}
          </Tabs.Trigger>
        </Tabs.List>

        {/* The manual override belongs to this monitoring room only. */}
        <BandwidthToggle roomId={roomId} />

        {/* Overview Tab Content */}
        <Tabs.Content className='flex flex-col gap-16 overflow-y-auto' value={Tab.Overview}>
          <div className='grid grid-cols-2 grid-rows-2 gap-12'>
            <button
              className='flex h-56 items-center rounded-4 bg-base-200 p-12'
              onClick={() => addVideo()}
            >
              <div className='flex flex-1 items-center justify-between leading-100'>
                <div className='text-sm font-medium'>Add video source</div>
                <i className='i-mdi-add text-primary' />
              </div>
            </button>
            <button
              className='flex h-56 items-center rounded-4 bg-base-200 p-12'
              onClick={() => addAudio()}
            >
              <div className='flex flex-1 items-center justify-between leading-100'>
                <div className='text-sm font-medium'>Add audio source</div>
                <i className='i-mdi-add text-primary' />
              </div>
            </button>
            <div className='flex h-56 items-center rounded-4 bg-base-200 p-12'>
              <div className='flex-1 text-muted'>
                <div className='flex items-center justify-between leading-100'>
                  <div className='text-sm font-medium'>Recording</div>
                </div>
                <div className='mt-2 flex justify-between'>
                  <div>--</div>
                  <i className='i-mdi-arrow-right' />
                </div>
              </div>
            </div>
            <div className='flex h-56 items-center rounded-4 bg-base-200 p-12'>
              <div className='flex-1 text-muted'>
                <div className='flex items-center justify-between leading-100'>
                  <div className='text-sm font-medium'>Output</div>
                </div>
                <div className='mt-2 flex justify-between'>
                  <div>--</div>
                  <i className='i-mdi-arrow-right' />
                </div>
              </div>
            </div>
          </div>
        </Tabs.Content>

        {/* Video Tab Content */}
        <Tabs.Content className='flex flex-col gap-16 overflow-y-auto' value={Tab.Video}>
          {summary.videoDevices
            ?.map(
              (device) =>
                !isTransient(device.id as Device['id']) && (
                  <DeviceSummaryVideo
                    agents={agents}
                    device={device}
                    key={`device_summary_${device.id}`}
                    roomId={roomId}
                    onClickSettings={handleOpenSettings}
                    onClickApplySuggestedSettings={handleApplySuggestedSettings}
                    onClickEnableVideo={handleEnableVideoForDevice}
                    summary={summary}
                  />
                )
            )
            .filter((element) => !!element)}
        </Tabs.Content>

        {/* Audio Tab Content */}
        <Tabs.Content className='flex flex-col gap-16 overflow-y-auto' value={Tab.Audio}>
          {summary.audioDevices?.map((device) => (
            <DeviceSummaryAudio
              agents={agents}
              device={device}
              key={`device_summary_${device.id}`}
              roomId={roomId}
              onClickSettings={handleOpenSettings}
              summary={summary}
            />
          ))}
        </Tabs.Content>

        {/* Recording Tab Content */}
        <Tabs.Content className='flex flex-col gap-16 overflow-y-auto' value={Tab.Recording}>
          <Recording sources={recordingDevices} />
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
};
